// Keyword gaps found in September 2026 research (Semrush US):
//
//  * "palworld shirt" (10/mo, KD 0) is dominated by in-game clothing intent, not
//    merch: "where to buy thermal shirt palworld" 20/mo, "where to get cold
//    resistant shirt palworld" 20/mo, "where to get thermal shirt palworld"
//    20/mo, "how to get heat resistant shirt palworld" 10/mo — all KD 0. The
//    existing gear-heat-resistant-outfit / gear-cold-resistant-outfit guides do
//    not use the word "shirt" anywhere, so the shirt phrasing was uncovered.
//  * "palworld tcg" 1,000/mo KD 33, "palworld trading cards" 260/mo (advertiser
//    competition 0.88), "pal card" 390/mo. Competitor card trackers answer
//    "what is it worth" but not "why did it move" — the price-shift angle is
//    the open slot next to our existing price-tracking guides.
//
// Structure matches src/lib/guides-merch-2026.ts: five AEO-shaped articles per
// topic (overview, step-by-step, reference, mistakes, FAQ). No invented prices,
// print runs or patch numbers.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-thermal-shirt-guide",
    title: "Palworld Thermal Shirt Guide",
    desc: "Where to get the heat resistant and cold resistant shirts in Palworld, which technology tier unlocks each one, and what to wear instead before you can craft them.",
    keywords:
      "palworld thermal shirt, where to buy thermal shirt palworld, where to get thermal shirt palworld, cold resistant shirt palworld, heat resistant shirt palworld, how to get shirts in palworld, palworld temperature clothing",
    hub: "/items",
    hubLabel: "Palworld item database",
    tag: "Gear",
    question: "Where do you get a thermal shirt in Palworld?",
    answer:
      "Palworld's \"thermal shirt\" is the temperature-resistant clothing line: a heat resistant outfit for desert and volcanic regions and a cold resistant outfit for snowfields. You craft them yourself at a workbench once the matching technology is unlocked with technology points, using cloth plus the region material the outfit protects against. Wandering merchants sell the cloth and the low-tier pelts, but the outfit itself is a crafted item, not a shop purchase — which is why searching for a place to buy one turns up nothing.",
    steps: [
      [
        "Decide which temperature is actually killing you",
        "Heat and cold are separate resistances with separate outfits. The status icon on your health bar tells you which one you need before you spend technology points on the wrong tier.",
      ],
      [
        "Unlock the outfit in the technology menu",
        "Open the technology tab, find the resistance outfit at your level band, and spend technology points. Nothing about the outfit is craftable until the recipe is unlocked.",
      ],
      [
        "Stock cloth before you travel",
        "Every resistance outfit is cloth-based. Set up a wool or fibre loop at your base so you can re-craft and upgrade instead of making one trip's worth.",
      ],
      [
        "Gather the region material the tier asks for",
        "Higher resistance tiers ask for materials from the biome they protect you in, so you will make one trip in the weaker outfit before you can craft the stronger one.",
      ],
      [
        "Craft at the workbench, then equip in the armour slot",
        "The outfit occupies the armour slot, so it replaces your defensive armour. Carry both and swap by region rather than wearing one everywhere.",
      ],
      [
        "Bridge the gap with a Pal and a campfire",
        "Before the recipe is unlocked, a fire-element Pal in your party and hot food handle cold, and a water or ice Pal handles heat well enough to survive a short trip.",
      ],
    ],
    data: [
      ["Item type", "Armour-slot clothing, crafted at a workbench — not sold as a finished item by merchants."],
      ["Heat version", "Heat resistant outfit line, for desert and volcanic regions."],
      ["Cold version", "Cold resistant outfit line, for snowfield regions and night-time cold."],
      ["Core material", "Cloth, produced from wool or fibre; higher tiers add biome-specific materials."],
      ["Unlock method", "Technology points spent in the technology menu at the matching level band."],
      ["Trade-off", "It uses your armour slot, so resistance clothing costs you defence while worn."],
      ["Stopgap", "Party Pal element plus cooked food covers a single trip until the recipe is unlocked."],
    ],
    pitfalls: [
      [
        "Hunting for a shop that sells the shirt",
        "Merchants sell materials, not the finished resistance outfit. Spend the technology point and craft it instead of walking the merchant routes.",
      ],
      [
        "Crafting the wrong resistance",
        "Heat and cold outfits do not substitute for each other. Read the status icon first, then unlock that side.",
      ],
      [
        "Wearing resistance clothing into a boss fight",
        "It replaces your armour, so your defence drops. Swap back to armour once you are out of the hostile biome.",
      ],
      [
        "Travelling with one outfit and no cloth",
        "Clothing takes durability damage. Carry spare cloth so you can re-craft at a portable workbench rather than turning back.",
      ],
    ],
    faq: [
      [
        "Where do you buy a thermal shirt in Palworld?",
        "You do not buy it. The heat resistant and cold resistant outfits are crafted at a workbench after unlocking the recipe with technology points. Merchants only stock the cloth and pelts that go into them.",
      ],
      [
        "How do you get the cold resistant shirt in Palworld?",
        "Unlock the cold resistant outfit in the technology menu, gather cloth plus the cold-region material its tier lists, then craft it at a workbench and equip it in the armour slot.",
      ],
      [
        "How do you get the heat resistant shirt in Palworld?",
        "Same route as the cold version: unlock the heat resistant outfit with technology points, craft it from cloth and the desert-region material, and equip it before entering volcanic or desert areas.",
      ],
      [
        "Can I survive cold without the shirt?",
        "For a short trip, yes. Keep a fire-element Pal in your party, eat hot cooked food and build campfires along the route. It is not enough for extended night-time exploration in the snowfields.",
      ],
      [
        "Does resistance clothing stack with armour?",
        "No. Resistance outfits occupy the armour slot, so you wear one or the other. Carry both and swap by region.",
      ],
      [
        "Which shirt tier should I craft first?",
        "The lowest tier for the biome you are heading into now. Higher tiers need materials from that biome, so the weaker outfit is what gets you in to gather them.",
      ],
    ],
  },
  {
    slug: "palworld-card-price-shifts",
    title: "Palworld Card Price Shifts",
    desc: "Why Palworld trading card prices move: the five events that shift a card's price, how to read a shift on the Pal Stars chart, and how to tell a real move from a thin-sample blip.",
    keywords:
      "palworld card price shifts, palworld trading cards price, pal card price, palworld tcg price changes, palworld card market trend, why did my palworld card price change, palworld card price drop",
    hub: "/prices",
    hubLabel: "Palworld TCG price chart",
    tag: "TCG",
    question: "Why do Palworld card prices shift?",
    answer:
      "Five things move a Palworld card's price: new product entering the market, a printing being reprinted or promo-distributed, tournament and deck-list demand, grading supply as slabs enter the population report, and plain listing thinness. Pal Stars separates them by storing one validated price point per card per source per day, so you can see whether a move is broad — every card in the set drifting the same way — or a single card reacting to its own supply.",
    steps: [
      [
        "Read the move on the card's own chart first",
        "Open the card on the price chart and check the 7- and 30-day columns. A one-day spike with a low listing count is noise, not a shift.",
      ],
      [
        "Check the listing count next to the price",
        "Thin-sample cards move on a single optimistic seller. The listing count printed with the row tells you how much the number deserves.",
      ],
      [
        "Compare the card against the index",
        "If the Pal Stars TCG Price Index moved the same way over the same window, you are looking at a market-wide shift rather than something specific to your card.",
      ],
      [
        "Separate asking prices from completed sales",
        "Asking prices show seller intent and move first. Completed-sale averages, sourced from TCGplayer, confirm whether buyers actually paid the new number.",
      ],
      [
        "Separate raw copies from graded slabs",
        "A graded price is a different product. Switch the board to the graded view rather than reading a slab result as a raw-market move.",
      ],
      [
        "Re-check after the next daily sync",
        "The board refreshes automatically every day. A shift that survives two syncs with a healthy listing count is a real repricing.",
      ],
    ],
    data: [
      ["New product", "A set or deck release adds supply across the whole product line and usually softens singles broadly."],
      ["Reprints and promos", "A card handed out again at events or bundled in product gains supply set by attendance, not print run — so it moves faster than booster cards."],
      ["Deck demand", "Playable staples move on deck lists; chase parallels move on collector demand, and the two do not have to agree."],
      ["Grading supply", "As slabs enter a grader's population report, the graded premium compresses even when the raw price is flat."],
      ["Listing thinness", "Few active listings means one seller can swing the median; a thin-sample flag is a reliability warning, not a price signal."],
      ["Data cadence", "One validated price point per card per source per day, which is what makes the 7- and 30-day movement columns comparable."],
      ["Sold data", "Completed-sale averages come from TCGplayer only. eBay completed-sale data is not used anywhere on Pal Stars."],
    ],
    pitfalls: [
      [
        "Treating one day as a trend",
        "A single sync can catch one unusual listing. Wait for the move to survive the next daily refresh before acting on it.",
      ],
      [
        "Comparing a graded result to a raw price",
        "Slabs are a separate market. Use the graded view, and read the graded multiple rather than mixing the two numbers.",
      ],
      [
        "Reading a high asking price as market value",
        "The market column is the median of validated listings. The optimistic listing sits in the high column by design.",
      ],
      [
        "Ignoring parallels when comparing prices",
        "A parallel treatment of the same Pal is a different printing with its own supply. Match the exact printed card number before concluding a price moved.",
      ],
    ],
    faq: [
      [
        "Why did my Palworld card price drop overnight?",
        "Usually new supply or a thin sample. Check the listing count on the row: if it is low, one seller relisting cheaply can move the median. If the count is healthy and the index dropped too, the whole market softened.",
      ],
      [
        "How often do Palworld card prices update?",
        "Pal Stars syncs validated marketplace data automatically every day and stores one point per card per source, which is what builds the 7-day and 30-day movement columns.",
      ],
      [
        "Does a tournament result move Palworld card prices?",
        "It moves playable cards, not chase parallels. Deck-list demand shows up on staples first, while collector printings track scarcity and event distribution instead.",
      ],
      [
        "Are asking prices or sold prices the real value?",
        "Asking prices tell you what sellers want today and move first. Completed-sale averages from TCGplayer tell you what buyers accepted, so they confirm a shift rather than predict it.",
      ],
      [
        "Why is a promo card more volatile than a booster card?",
        "Promo supply is set by event attendance rather than a print run, so a single event weekend can add or withhold enough copies to reprice the card.",
      ],
      [
        "Where can I see whether the whole Palworld card market is rising?",
        "Use the Pal Stars TCG Price Index, an equal-weight benchmark with 7-, 14- and 30-day snapshots. Individual card rows tell you about one printing; the index tells you about the category.",
      ],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually confirmed", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Open ${t.hub} and work from the live data there rather than a remembered number.`,
        },
      ],
      externalLinks: pool(4),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost the most on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(5),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(3),
    },
  ];
}

export const SHIRT_SHIFT_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-09-12",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each cluster supports, for internal-link rendering. */
export const SHIRT_SHIFT_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
